import { FormData, Morfi } from '@hugendubel/morfi';
import {
    FormButtons,
    FormInput,
    FormSelect,
    Input,
    SelectOption,
    Toaster,
    TrackingForm,
} from '@hugendubel/react-basic';
import { TRACK_ID, Track } from '@hugendubel/tracking_client';
import { __, __string } from 'first-intl';
import { useCallback, useState } from 'react';
import { Messages } from '../../../../messages';
import { Store } from '../../../../store';
import { WS } from '../../../../ws/WS';
import { WsAddress } from '../../../../ws/result/WsAddress';
import { WsSalutation } from '../../../../ws/result/WsSalutation';
import { updateDeliveryAddresses } from './AddCompanyAddressForm';
import { PrivateAddressFormValidations } from './common';

const salutationOptions: Array<SelectOption<WsSalutation['code']>> = [
    { label: Messages.SALUTATION_EMPTY, value: '' }, // no value selected
    { label: Messages.SALUTATION_MRS, value: WsSalutation.MRS.code },
    { label: Messages.SALUTATION_MR, value: WsSalutation.MR.code },
    { label: Messages.SALUTATION_NONBINARY, value: WsSalutation.NONBINARY.code },
    { label: Messages.SALUTATION_UNDEFINED, value: WsSalutation.UNDEFINED.code },
];

type AddPrivateAddressFormValues = {
    salutation: string;
    firstName: string;
    lastName: string;
    addition: string;
    street: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    phone: string;
    mobilePhone: string;
};
const initialValues: AddPrivateAddressFormValues = {
    salutation: '',
    firstName: '',
    lastName: '',
    addition: '',
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    phone: '',
    mobilePhone: '',
};
const { Form, Fields } = TrackingForm.create<AddPrivateAddressFormValues>(
    initialValues,
    TRACK_ID('2019-07-31 12:22:10')
);

const onSubmitFailed = (error: any): void => {
    if (!Morfi.isValidationError(error)) {
        Track.error(TRACK_ID('2018-09-10 10:33:45'), 'Address could not be created: ' + error.message);
        Toaster.open(Messages.ADDRESS_CREATION_FAILED);
    }
};

export type AddPrivateAddressFormProps = { onClose: () => void };

export const AddPrivateAddressForm = ({ onClose }: AddPrivateAddressFormProps) => {
    const [data, setData] = useState<FormData<AddPrivateAddressFormValues>>({ values: initialValues, errors: {} });

    const onSubmit = useCallback(
        ({
            salutation,
            firstName,
            lastName,
            addition,
            street,
            houseNumber,
            zipCode,
            city,
            phone,
            mobilePhone,
        }: AddPrivateAddressFormValues): Promise<void> =>
            WS.createAddress({
                salutation,
                firstName,
                lastName,
                companyName: '',
                addition,
                street,
                houseNumber,
                zipCode,
                city,
                phone,
                mobilePhone,
            }).then((address: WsAddress) => {
                Store.set(({ deliveryAddresses }) => updateDeliveryAddresses(deliveryAddresses, address));
                onClose();
            }),
        [onClose]
    );

    // filter out placeholder option if a value has already been selected
    const filteredSalutationOptions = data.values.salutation
        ? salutationOptions.filter(option => option.value)
        : salutationOptions;

    return (
        <Form
            validation={PrivateAddressFormValidations}
            onSubmit={onSubmit}
            onSubmitFailed={onSubmitFailed}
            data={data}
            onChange={setData}
        >
            <div>
                <div className="row">
                    <div className="col col-xs-12 col-sm-6">
                        <FormSelect
                            className="form-control auto-width"
                            Field={Fields.salutation}
                            options={filteredSalutationOptions}
                            label="form.label.salutation"
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col col-xs-12 col-sm-6">
                        <FormInput
                            Field={Fields.firstName}
                            autoFocus
                            label="form.label.firstName"
                            autocomplete="given-name"
                        />
                    </div>
                    <div className="col col-xs-12 col-sm-6">
                        <FormInput Field={Fields.lastName} label="form.label.lastName" autocomplete="family-name" />
                    </div>
                </div>
            </div>
            <div>
                <div className="row">
                    <div className="col col-xs-12 col-sm-6">
                        <div className="input-group auto-small">
                            <FormInput
                                className="form-control grow required"
                                Field={Fields.street}
                                label="form.label.street"
                                autocomplete="address-line1"
                            />
                            <FormInput
                                className="form-control size8 required"
                                Field={Fields.houseNumber}
                                label="form.label.houseNumber"
                                autocomplete="address-line2"
                            />
                        </div>
                    </div>
                    <div className="col col-xs-12 col-sm-6">
                        <FormInput Field={Fields.addition} label="form.label.addition" autocomplete="address-line3" />
                    </div>
                </div>
                <div className="row">
                    <div className="col col-xs-12 col-sm-6">
                        <div className="input-group small-auto">
                            <FormInput
                                className="form-control size7"
                                Field={Fields.zipCode}
                                label="form.label.zipCode"
                                autocomplete="postal-code"
                            />
                            <FormInput
                                className="form-control grow"
                                Field={Fields.city}
                                label="form.label.city"
                                autocomplete="address-level2"
                            />
                        </div>
                    </div>
                    <div className="col col-xs-12 col-sm-6">
                        <Input
                            className="form-control"
                            label="form.label.country"
                            disabled={true}
                            value={__string('form.value.country.de')}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col col-xs-12 col-sm-6">
                        <FormInput Field={Fields.phone} label="form.label.phone" />
                    </div>
                    <div className="col col-xs-12 col-sm-6">
                        <FormInput Field={Fields.mobilePhone} label="form.label.mobilePhone" />
                    </div>
                </div>

                <div className="required-info">{__('form.required.info')}</div>
            </div>
            <FormButtons onAbort={onClose} disabled={Morfi.hasErrors(data)} loading={data.submitting} />
        </Form>
    );
};
